import { supabase } from '../lib/supabaseClient';

// -- Types --
export type Month = {
  id: string;
  month_key: string;
  reste_a_vivre: number;
  sur_le_compte: number;
  is_active: boolean;
};

export type Expense = {
  id: string;
  month_id: string;
  budget_id: string | null;
  title: string;
  amount: number;
  category: string;
  due_date: string;
  is_paid: boolean;
  is_budgetized: boolean;
};

export type Budget = {
  id: string;
  month_id: string;
  name: string;
  total: number;
  type: 'commun' | 'perso';
  owner: string | null;
};

export type Advance = {
  id: string;
  payer: string;
  for_whom: string;
  amount: number;
  reason: string;
  settled: boolean;
};

export type Repayment = {
  id: string;
  advance_id: string;
  amount: number;
};

export type HouseholdSettings = {
  id: string;
  default_salaries: any[];
  default_expenses_template: any[];
  payment_sources?: any[];
};

// -- Services --

export const getSettings = async (): Promise<HouseholdSettings | null> => {
  const { data, error } = await supabase.from('household_settings').select('*').limit(1);
  if (error) {
    console.error('Error fetching settings:', error);
    return null;
  }
  if (data && data.length > 0) {
    return data[0];
  }
  // Create default settings row
  const { data: inserted, error: insertError } = await supabase
    .from('household_settings')
    .insert([{ default_salaries: [], default_expenses_template: [] }])
    .select()
    .single();
  if (insertError) {
    console.error('Error creating default settings:', insertError);
    return null;
  }
  return inserted;
};

export const updateSettings = async (id: string, updates: Partial<HouseholdSettings>) => {
  const { error } = await supabase.from('household_settings').update(updates).eq('id', id);
  if (error) console.error('Error updating settings:', error);
};

export const getMonth = async (monthKey: string): Promise<Month | null> => {
  const { data, error } = await supabase.from('months').select('*').eq('month_key', monthKey).single();
  if (error && error.code !== 'PGRST116') console.error('Error fetching month:', error);
  return data;
};

export const createMonth = async (month: Omit<Month, 'id'>): Promise<Month | null> => {
  const { data, error } = await supabase.from('months').insert([month]).select().single();
  if (error) {
    console.error('Error creating month:', error);
    return null;
  }
  return data;
};

export const updateMonth = async (id: string, updates: Partial<Month>) => {
  const { error } = await supabase.from('months').update(updates).eq('id', id);
  if (error) console.error('Error updating month:', error);
};

export const getExpenses = async (monthId: string): Promise<Expense[]> => {
  const { data, error } = await supabase.from('expenses').select('*').eq('month_id', monthId);
  if (error) {
    console.error('Error fetching expenses:', error);
    return [];
  }
  return data || [];
};

export const createExpense = async (expense: Omit<Expense, 'id'>): Promise<Expense | null> => {
  const { data, error } = await supabase.from('expenses').insert([expense]).select().single();
  if (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
  return data;
};

export const updateExpense = async (id: string, updates: Partial<Expense>) => {
  const { error } = await supabase.from('expenses').update(updates).eq('id', id);
  if (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

export const deleteExpense = async (id: string) => {
  const { error } = await supabase.from('expenses').delete().eq('id', id);
  if (error) console.error('Error deleting expense:', error);
};

export const getBudgets = async (monthId: string): Promise<Budget[]> => {
  const { data, error } = await supabase.from('budgets').select('*').eq('month_id', monthId);
  if (error) {
    console.error('Error fetching budgets:', error);
    return [];
  }
  return data || [];
};

export const createBudgets = async (budgets: Omit<Budget, 'id'>[]) => {
  const { error } = await supabase.from('budgets').insert(budgets);
  if (error) {
    console.error('Error creating budgets:', error);
    throw error;
  }
};

export const updateBudget = async (id: string, updates: Partial<Budget>) => {
  const { error } = await supabase.from('budgets').update(updates).eq('id', id);
  if (error) console.error('Error updating budget:', error);
};

export const deleteBudget = async (id: string) => {
  const { error } = await supabase.from('budgets').delete().eq('id', id);
  if (error) console.error('Error deleting budget:', error);
};

export const getAdvances = async (): Promise<Advance[]> => {
  const { data, error } = await supabase.from('advances').select('*').order('created_at', { ascending: false });
  if (error) {
    console.error('Error fetching advances:', error);
    return [];
  }
  return data || [];
};

export const getRepaymentsForAdvance = async (advanceId: string): Promise<Repayment[]> => {
  const { data, error } = await supabase.from('repayments').select('*').eq('advance_id', advanceId);
  if (error) {
    console.error('Error fetching repayments:', error);
    return [];
  }
  return data || [];
};

export const createAdvance = async (advance: Omit<Advance, 'id'>): Promise<Advance | null> => {
  const { data, error } = await supabase.from('advances').insert([advance]).select().single();
  if (error) console.error('Error creating advance:', error);
  return data;
};

export const updateAdvance = async (id: string, updates: Partial<Advance>) => {
  const { error } = await supabase.from('advances').update(updates).eq('id', id);
  if (error) console.error('Error updating advance:', error);
};

export const deleteAdvance = async (id: string) => {
  const { error } = await supabase.from('advances').delete().eq('id', id);
  if (error) console.error('Error deleting advance:', error);
};

export const createRepayment = async (repayment: Omit<Repayment, 'id'>) => {
  const { error } = await supabase.from('repayments').insert([repayment]);
  if (error) console.error('Error creating repayment:', error);
};

export const deleteIncomesForMonth = async (monthId: string) => {
  const { error } = await supabase.from('expenses').delete().eq('month_id', monthId).eq('category', 'revenu');
  if (error) console.error('Error deleting incomes:', error);
};

export const setActiveMonthInDB = async (monthId: string) => {
  const { error: deactivateError } = await supabase.from('months').update({ is_active: false }).neq('id', monthId);
  if (deactivateError) console.error('Error deactivating other months:', deactivateError);
  
  const { error: activateError } = await supabase.from('months').update({ is_active: true }).eq('id', monthId);
  if (activateError) console.error('Error activating month:', activateError);
};

export const getActiveMonthFromDB = async (): Promise<Month | null> => {
  const { data, error } = await supabase.from('months').select('*').eq('is_active', true).limit(1);
  if (error) {
    console.error('Error fetching active month:', error);
    return null;
  }
  return data && data.length > 0 ? data[0] : null;
};

export const getAnalyticsData = async (): Promise<{
  months: Month[];
  expenses: Expense[];
  budgets: Budget[];
}> => {
  const [monthsRes, expensesRes, budgetsRes] = await Promise.all([
    supabase.from('months').select('*').order('month_key', { ascending: true }),
    supabase.from('expenses').select('*'),
    supabase.from('budgets').select('*')
  ]);
  
  return {
    months: monthsRes.data || [],
    expenses: expensesRes.data || [],
    budgets: budgetsRes.data || []
  };
};
